import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  HubspotCompaniesResponseDto,
  HubspotConnectionSaveResponseDto,
  HubspotConnectionWrite,
  HubspotMessageResponseDto,
  HubspotOAuthStartResponseDto,
  HubspotPipelinesResponseDto,
  HubspotPortalLinksResponseDto,
  HubspotProjectConnectionResponseDto,
} from '../models/hubspot.dto';

/**
 * The HubSpot integration: an organization connects HubSpot accounts ("portal links"), and each
 * project attaches to one company in one of them.
 */
@Injectable({
  providedIn: 'root',
})
export class HubspotService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiHost}/api`;

  // --- Organization-level endpoints ---

  public getPortalLinks(organizationId: string): Observable<HubspotPortalLinksResponseDto> {
    return this.http.get<HubspotPortalLinksResponseDto>(`${this.apiUrl}/organizations/${organizationId}/hubspot/`);
  }

  public getOAuthStartUrl(organizationId: string): Observable<HubspotOAuthStartResponseDto> {
    return this.http.get<HubspotOAuthStartResponseDto>(`${this.apiUrl}/hubspot/oauth/start/`, {
      params: { organization_id: organizationId },
    });
  }

  public disconnectPortalLink(organizationId: string, linkId: string): Observable<HubspotMessageResponseDto> {
    return this.http.delete<HubspotMessageResponseDto>(
      `${this.apiUrl}/organizations/${organizationId}/hubspot/${linkId}/`,
    );
  }

  /** Up to 20 companies whose name or domain matches `q`; a blank `q` lists the first 20. */
  public searchCompanies(organizationId: string, linkId: string, q: string): Observable<HubspotCompaniesResponseDto> {
    return this.http.get<HubspotCompaniesResponseDto>(
      `${this.apiUrl}/organizations/${organizationId}/hubspot/${linkId}/companies/`,
      { params: { q } },
    );
  }

  public getPipelines(organizationId: string, linkId: string): Observable<HubspotPipelinesResponseDto> {
    return this.http.get<HubspotPipelinesResponseDto>(
      `${this.apiUrl}/organizations/${organizationId}/hubspot/${linkId}/pipelines/`,
    );
  }

  // --- Project-level endpoints ---

  public getProjectConnection(projectId: string): Observable<HubspotProjectConnectionResponseDto> {
    return this.http.get<HubspotProjectConnectionResponseDto>(`${this.apiUrl}/projects/${projectId}/hubspot/`);
  }

  /** `PUT` attaches the project or replaces its whole connection; `partial` sends a `PATCH`. */
  public saveProjectConnection(
    projectId: string,
    body: HubspotConnectionWrite,
    partial = false,
  ): Observable<HubspotConnectionSaveResponseDto> {
    const url = `${this.apiUrl}/projects/${projectId}/hubspot/`;
    return partial
      ? this.http.patch<HubspotConnectionSaveResponseDto>(url, body)
      : this.http.put<HubspotConnectionSaveResponseDto>(url, body);
  }

  public removeProjectConnection(projectId: string): Observable<HubspotMessageResponseDto> {
    return this.http.delete<HubspotMessageResponseDto>(`${this.apiUrl}/projects/${projectId}/hubspot/`);
  }
}
